import { MutableRefObject, useMemo } from 'react';
import { TabsProps } from './props';
import styles from './styles.module.scss';
import Tab from './Tab';
import { useCustomCarousel, useIndicator } from '@starsoft/common/hooks';
import { SettingLayoutComponentId } from '../../models';

/**
 * Tabs Component
 * A customizable tab navigation component that supports different visual styles, layouts and interactions.
 * Features include draggable carousel functionality, custom tab rendering, and visual indicators.
 *
 * @component
 * @template T - Type for tab value/data
 * @param {Object} props - Component props
 * @param {Array<T>} props.tabs - Array of tab items to display
 * @param {boolean} props.isTiny - Whether to use tiny styling
 * @param {Function} props.setTab - Callback to handle tab selection
 * @param {boolean} props.isSmall - Whether to use small styling
 * @param {string} props.version - Version ID to control component appearance
 * @param {boolean} props.isColumn - Whether tabs should be arranged vertically
 * @param {boolean} props.disabled - Whether tabs are disabled
 * @param {Function} props.renderTab - Custom tab render function
 * @param {T} props.currentTab - Currently selected tab
 * @param {boolean} props.invertColors - Whether to invert the color scheme
 * @param {boolean} props.useMaxContent - Whether to use max-content width
 * @param {string} props.i18nNamespace - i18n namespace for translations
 * @param {string} props.id - Unique identifier for the tabs component
 * @returns {JSX.Element} The rendered tabs component
 *
 * @example
 * interface TabItem {
 *   value: string;
 *   label: string;
 * }
 *
 * const tabs: TabItem[] = [
 *   { value: 'tab1', label: 'First Tab' },
 *   { value: 'tab2', label: 'Second Tab' }
 * ];
 *
 * <Tabs<TabItem>
 *   tabs={tabs}
 *   currentTab={tabs[0]}
 *   setTab={(tab) => function('Selected tab:', tab)}
 *   version={SettingLayoutComponentId.TabsV1.toString()}
 *   isSmall={false}
 *   isTiny={false}
 *   isColumn={false}
 *   disabled={false}
 *   invertColors={false}
 *   useMaxContent={false}
 *   i18nNamespace="common"
 * />
 */
export function Tabs<T>({
  tabs,
  isTiny,
  setTab,
  isSmall,
  version,
  isColumn,
  disabled,
  renderTab,
  currentTab,
  invertColors,
  useMaxContent,
  i18nNamespace = 'common',
  id = `tab-${tabs.toString()}`,
}: TabsProps<T>) {
  // Custom hook for carousel drag functionality
  const {
    containerRef,
    handleMouseDown,
    handleMouseLeave,
    handleMouseMove,
    handleMouseUp,
    isDragging,
  } = useCustomCarousel();

  // Hook for managing the tab indicator position and animation
  const indicatorRef: MutableRefObject<HTMLDivElement> = useIndicator({
    tabs,
    currentTab,
    containerRef,
    isDisabled: version !== SettingLayoutComponentId.TabsV1?.toString(),
  });

  // Memoized tab elements to prevent unnecessary re-renders
  const Tabs = useMemo(
    () =>
      tabs.map(carouselTab =>
        !!renderTab ? (
          renderTab({
            setTab,
            version,
            isSmall,
            currentTab,
            i18nNamespace,
            tab: carouselTab,
          })
        ) : (
          <Tab
            setTab={setTab}
            isTiny={isTiny}
            tab={carouselTab}
            isSmall={isSmall}
            version={version}
            disabled={disabled}
            currentTab={currentTab}
            invertColors={invertColors}
            i18nNamespace={i18nNamespace}
            useMaxContent={useMaxContent}
            key={`game-section-carousel-tab-${carouselTab.value}`}
          />
        ),
      ),
    [
      tabs,
      setTab,
      isTiny,
      version,
      isSmall,
      disabled,
      renderTab,
      currentTab,
      invertColors,
      i18nNamespace,
      useMaxContent,
    ],
  );

  return (
    <div
      className={`${styles.container} ${isTiny ? styles['container--tiny'] : ''} ${version === SettingLayoutComponentId.TabsV3?.toString() ? styles['container--useContainer'] : ''} ${isDragging ? styles['container--dragging'] : ''} ${useMaxContent ? styles['container--max-content'] : ''} ${invertColors && version === SettingLayoutComponentId.TabsV3?.toString() ? styles['container--invert-colors'] : ''} ${version === SettingLayoutComponentId.TabsV1?.toString() ? styles['container--with-indicator'] : ''} ${isColumn ? styles['container--column'] : ''}`}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      ref={containerRef}
    >
      {Tabs}

      {version === SettingLayoutComponentId.TabsV1?.toString() && (
        <div
          className={styles.container__indicator}
          id={id}
          ref={indicatorRef}
        />
      )}
    </div>
  );
}
